import type { EventObject, MetaObject, SetupTypes } from "xstate";
import { assign, setup } from "xstate";

export const CHAT_V8_NEAR_BOTTOM_VIEWPORT_FRACTION = 0.5;

export type T_TranscriptScrollContext = {
  contentHeight: number;
  hasContentMeasurement: boolean;
  hasViewportMeasurement: boolean;
  isNearBottom: boolean;
  scrollToOffset: (offset: number) => void;
  viewportHeight: number;
};

export type T_TranscriptScrollEvent =
  | { type: "CONTENT_SIZE_CHANGED"; contentHeight: number; pin: boolean }
  | { type: "JUMP_TO_BOTTOM" }
  | { type: "LAYOUT"; viewportHeight: number }
  | { type: "SCROLLED"; isNearBottom: boolean }
  | { type: "USER_DRAG_STARTED" };

export type T_TranscriptScrollInput = {
  scrollToOffset: (offset: number) => void;
};

const transcriptScrollSetupTypes: SetupTypes<
  T_TranscriptScrollContext,
  T_TranscriptScrollEvent,
  Record<never, string>,
  string,
  T_TranscriptScrollInput,
  unknown,
  EventObject,
  MetaObject
> = {};

/**
 * Platform-neutral ownership for transcript positioning and follow mode.
 * Native and web adapters report measurements/reader intent; this machine is
 * the sole lifecycle source for initial anchoring and streaming pin behavior.
 */
export const transcriptScrollMachine = setup({
  types: transcriptScrollSetupTypes,
  actions: {
    recordContentHeight: assign(({ event }) => {
      if (event.type !== "CONTENT_SIZE_CHANGED") return {};
      return {
        contentHeight: event.contentHeight,
        hasContentMeasurement: true,
      };
    }),
    recordNearBottom: assign(({ event }) => {
      if (event.type !== "SCROLLED") return {};
      return { isNearBottom: event.isNearBottom };
    }),
    recordViewportHeight: assign(({ event }) => {
      if (event.type !== "LAYOUT") return {};
      return {
        hasViewportMeasurement: true,
        viewportHeight: event.viewportHeight,
      };
    }),
    scrollToBottom: ({ context }) => {
      context.scrollToOffset(
        Math.max(0, context.contentHeight - context.viewportHeight),
      );
    },
  },
  guards: {
    hasMeasurements: ({ context }) =>
      context.hasContentMeasurement && context.hasViewportMeasurement,
    readingShouldPin: ({ context, event }) =>
      event.type === "CONTENT_SIZE_CHANGED" &&
      event.pin &&
      context.isNearBottom,
  },
}).createMachine({
  id: "transcriptScroll",
  context: ({ input }) => ({
    contentHeight: 0,
    hasContentMeasurement: false,
    hasViewportMeasurement: false,
    isNearBottom: true,
    scrollToOffset: input.scrollToOffset,
    viewportHeight: 0,
  }),
  initial: "positioning",
  states: {
    anchored: {
      on: {
        CONTENT_SIZE_CHANGED: {
          actions: ["recordContentHeight", "scrollToBottom"],
        },
        LAYOUT: {
          actions: ["recordViewportHeight", "scrollToBottom"],
        },
        JUMP_TO_BOTTOM: { actions: "scrollToBottom" },
        SCROLLED: { actions: "recordNearBottom" },
        USER_DRAG_STARTED: { target: "reading" },
      },
    },
    positioning: {
      always: {
        actions: "scrollToBottom",
        guard: "hasMeasurements",
        target: "anchored",
      },
      on: {
        CONTENT_SIZE_CHANGED: { actions: "recordContentHeight" },
        LAYOUT: { actions: "recordViewportHeight" },
        SCROLLED: { actions: "recordNearBottom" },
      },
    },
    reading: {
      on: {
        CONTENT_SIZE_CHANGED: [
          {
            actions: ["recordContentHeight", "scrollToBottom"],
            guard: "readingShouldPin",
          },
          { actions: "recordContentHeight" },
        ],
        // Explicit reader intent to resume following: jump and re-anchor, so
        // subsequent streaming keeps the transcript pinned.
        JUMP_TO_BOTTOM: {
          actions: "scrollToBottom",
          target: "anchored",
        },
        LAYOUT: { actions: "recordViewportHeight" },
        SCROLLED: { actions: "recordNearBottom" },
      },
    },
  },
});
